/**
 * surgeryRecord.service.ts
 *
 * The per-plan Surgery record behind the patient chart's Surgery tab.
 * Separate from surgery.service.ts, which serves the cross-patient worklist.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

/** One per-field audit stamp. Null means the field has never been changed. */
export interface SurgeryAuditStamp {
  at: string;
  by: string | null;
}

export interface SurgeryConsult {
  consultRequestId: number;
  status: string;
  clearance: string;
  labsReviewed: string;
  crnaClearance: string;
  notes: {
    medicalClearance: string;
    crna: string;
    surgeon: string;
    coordinator: string;
  };
  listNote: string;
  leftImplant: string;
  rightImplant: string;
  /** Set once the case is removed from the Surgery list; a status save clears it. */
  endDate: string | null;
}

export interface SurgeryPlanHeader {
  planDisplayId: string | null;
  procedures: string;
  planType: string;
  stage: string;
  doctorName: string;
  coordinatorName: string;
  nurseName: string;
  /** Stored Pacific wall clock ("2026-10-05 07:30:00"); render with `facilityTimezone`. */
  preopDate: string | null;
  procedureDate: string | null;
  /** IANA zone of the plan's facility (else the practice's), in which legacy shows these times. */
  facilityTimezone: string | null;
  due: number;
  quote: number;
}

/**
 * The checklist's read-only money row. Legacy titles it "Balance Due" until a
 * payment exists, then "Final Payment"; its audit cell reads "Last paid <date>"
 * because nothing about it is stored on the consult.
 */
export interface SurgeryBalance {
  /** "Balance Due" while money is owed; "Final Payment" only once settled. */
  label: string;
  /**
   * The figure to display. The server decides it — the outstanding balance
   * whenever one is owed, otherwise the last payment. Do not re-derive it here:
   * picking the payment over the balance is the bug this replaced.
   */
  amount: number;
  due: number;
  lastPaidAmount: number | null;
  lastPaidOn: string | null;
}

/**
 * One row of the account-configured "Special Situations" checklist. Backed by
 * labs_checklist / labs_checklist_answers, which is append-only — every tick
 * writes a new row, so the table is its own audit trail.
 */
export interface SurgerySpecialSituation {
  id: number;
  title: string;
  checked: boolean;
  at: string | null;
  by: string | null;
}

/**
 * The vitals strip. Values arrive formatted (units included) because legacy
 * prints the units as part of the value, and BMI is computed server-side from
 * legacy's own constants rather than the metric definition.
 */
export interface SurgeryVitals {
  age: string;
  bmi: string;
  height: string;
  bloodPressure: string;
  weight: string;
  heartRate: string;
  added: string | null;
  temperature: string;
}

/**
 * One message on the consult. This is not every message on the plan: legacy
 * shows only those tagged to the consult at send time, plus the single newest
 * stage event. `body` is HTML; sanitize before rendering.
 */
export interface SurgeryCommLogEntry {
  commLogId: number;
  subject: string;
  body: string;
  sentAt: string | null;
  fromPatient: boolean;
  isStageEvent: boolean;
  author: string | null;
  hasAttachments: boolean;
}

/**
 * A read-only medical-history section. The Surgery tab shows only four of the
 * form's sections (Screening Questions, Medical History, Habits, GYN History);
 * the printable version shows all of them, so the two legitimately differ.
 */
export interface SurgeryHistorySection {
  title: string;
  fields: { label: string; value: string; type: 'text' | 'choice' | 'short' }[];
}

export interface SurgeryRecord {
  planId: number;
  /** Null when this plan has no surgical case yet — the first save creates one. */
  consult: SurgeryConsult | null;
  plan: SurgeryPlanHeader | null;
  /** Keyed by consult_requests column; null means never updated. */
  audit: Record<string, SurgeryAuditStamp | null>;
  checklist: Record<string, number>;
  balance: SurgeryBalance;
  specialSituations: SurgerySpecialSituation[];
  vitals: SurgeryVitals;
  commLog: SurgeryCommLogEntry[];
  history: SurgeryHistorySection[];
}

export function getSurgeryRecord(planId: number): Promise<SurgeryRecord> {
  return apiClient
    .get<ApiResponse<SurgeryRecord>>(`/surgery/${planId}`)
    .then((r) => r.data.data);
}

/**
 * Save only what changed. The server merges the delta, so fields absent here
 * keep whatever another user may have set since this page loaded.
 */
export function saveSurgeryRecord(
  planId: number,
  changes: Record<string, string | number>,
): Promise<{ planId: number; saved: string[]; audit: Record<string, SurgeryAuditStamp | null> }> {
  return apiClient
    .patch<ApiResponse<{ planId: number; saved: string[]; audit: Record<string, SurgeryAuditStamp | null> }>>(
      `/surgery/${planId}`,
      changes,
    )
    .then((r) => r.data.data);
}

/** Tick or untick one configured situation. Appends; never updates in place. */
export function setSpecialSituation(
  planId: number,
  itemId: number,
  checked: boolean,
): Promise<{ specialSituations: SurgerySpecialSituation[] }> {
  return apiClient
    .put<ApiResponse<{ specialSituations: SurgerySpecialSituation[] }>>(
      `/surgery/${planId}/special-situations/${itemId}`,
      { checked },
    )
    .then((r) => r.data.data);
}

/**
 * Download the printable record.
 *
 * Fetched as a blob because the endpoint is JWT-authenticated — a plain link
 * would drop the header. Legacy round-tripped a filename through the client and
 * read it straight off disk; the bytes come back directly here instead.
 */
export async function printSurgeryRecord(planId: number): Promise<void> {
  const response = await apiClient.get(`/surgery/${planId}/print`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `surgery-${planId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking in the same tick can cancel the download before the browser has
  // read the blob. Same delay as the quote spreadsheet download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** "Remove from Surgery List" — end-dates the case without changing its status. */
export function removeFromSurgeryList(planId: number): Promise<void> {
  return apiClient.delete(`/surgery/${planId}`).then(() => undefined);
}
